import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import "./styles/reportPageHeaders.css";
import { installAgentReferralConsentListener } from "./utils/agentReferralCookie";
import { getItem as getStoredItem, removeItem as removeStoredItem } from "./utils/storage";

// =========================================================================
// JOB-3719 (CDM Phase 3) — tenant subdomain bootstrap.
//
// When the URL is <slug>.cloudjobmanager.com or
// <slug>.clouddonormanager.com (or any *.local equivalent for dev),
// extract the slug and rewrite the React Router path to inject
// /<slug>/... at the start so it matches the /:CompanyUrl/* layout
// defined in routes.jsx. The auth flow + the rest of the app keep
// using `useParams().CompanyUrl` as if the slug came from the path.
//
// Runs BEFORE React mounts so the very first route match has the
// rewritten path. Idempotent: skips when the path already starts
// with /<slug>/.
//
// Gated on VITE_SUBDOMAIN_ROUTING_ENABLED so the code can land
// safely before the infra side (wildcard SSL + DNS + reverse-proxy
// host-matching) is in place. In environments without the env flag
// set, this is a no-op and the app keeps using path-based routing
// (cloudjobmanager.com/<slug>/...) unchanged.
// =========================================================================
(function bootstrapTenantSubdomain() {
  try {
    if (import.meta.env.VITE_SUBDOMAIN_ROUTING_ENABLED !== "true") return;
    const hostname = window.location?.hostname || "";
    if (!hostname || hostname === "localhost") return;
    const parts = hostname.split(".");
    // Need at least 3 parts (slug.domain.tld) to be a tenant subdomain.
    if (parts.length < 3) return;
    const slug = parts[0];
    // www. is not a tenant subdomain. Skip well-known platform
    // sub-hosts (admin, api, app, www).
    if (["www", "api", "admin", "app", "auth"].includes(slug.toLowerCase())) return;
    // Skip when the path already starts with /<slug>/ — idempotent.
    const path = window.location.pathname || "/";
    if (path === `/${slug}` || path.startsWith(`/${slug}/`)) return;
    const newPath = `/${slug}${path === "/" ? "" : path}`;
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    window.history.replaceState({}, "", `${newPath}${search}${hash}`);
  } catch (_err) {
    // Never break the app boot for a subdomain-routing helper.
  }
})();

// =========================================================================
// Agent referral attribution (Phase 1b) — consent-withdrawal handler.
//
// Registers a listener for the documented `cjm:consent-changed` event and
// deletes the first-party referral cookie when marketing consent is no longer
// granted. The server also deletes, but only on a request that reaches
// POST /v1/public/agent/click — and a visitor withdrawing consent inside the
// app fires no click, so without this the claim would survive a withdrawal
// until it expired.
//
// Inert until whoever owns the consent banner dispatches that event. With no
// banner the cookie is still written at the click (no refusal was given;
// decision 12) and lapses at 14 days. Never throws.
// See src/utils/agentReferralCookie.js and
// docs/agent-referral-consent-integration.md.
// =========================================================================
installAgentReferralConsentListener();

// Super admin isolation (docs/SUPERADMIN-ISOLATION.md): super admins do not
// sign in to this app any more. A super-admin session left in this domain's
// storage from before the split can no longer authenticate anywhere here, so
// drop it rather than let the router treat it as a signed-in user.
(function dropStaleSuperAdminSession() {
  try {
    const raw = getStoredItem("adminToken");
    if (!raw) return;
    const payload = JSON.parse(atob(raw.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload && payload.AdminId) {
      ["adminToken", "adminRefreshToken", "admin_id"].forEach((k) => removeStoredItem(k));
    }
  } catch (_err) {
    // not a JWT we can read: leave it to the normal session checks
  }
})();
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  setAxiosNavigator,
  FORCE_LOGOUT_EVENT,
  startSessionWatch,
} from "./utils/axiosInstance";
import { ThemeProvider, createTheme } from "@mui/material/styles";
// All 6 layouts (Auth + 5 role layouts) are eager so the app shell paints
// immediately on hard refresh — no waiting on a layout chunk download.
// This is now safe because:
//   • MuiTable no longer pulls ContractComponent/Index (3778 lines with
//     FullCalendar + react-big-calendar) — it imports NoDataFound from its
//     own small file instead.
//   • AIChat is lazy-loaded inside MuiTable and only mounts when the user
//     opens the chat panel.
//   • The per-page components stay React.lazy in routes.jsx — that's the
//     real win.
import Auth from "./components/Layouts/Auth";
import Admin from "./components/Layouts/Admin";
import Client from "./components/Layouts/Client";
import StaffMember from "./components/Layouts/StaffMember";
import Donor from "./components/Layouts/Donor";
// Standalone routes that are not part of a role layout — these can stay
// lazy because they're rarely the entry point.
const Plan = React.lazy(() => import("./Views/Admin/Plan/Index"));
const PlanPurchase = React.lazy(() => import("./Views/Admin/Plan/PlanPurchase"));
const InvoicePayment = React.lazy(() => import("./Views/Admin/Invoice/InvoiceLivePayment"));
const LinkPayment = React.lazy(() => import("./Views/Admin/Client/RecurringPayments/LinkPayment"));
const PublicDonation = React.lazy(() => import("./Views/Public/PublicDonation"));
const NewsletterUnsubscribe = React.lazy(() => import("./Views/Public/NewsletterUnsubscribe"));
// JOB-3721 (CDM Phase 5) — public peer-to-peer fundraiser page.
const FundraiserPage = React.lazy(() => import("./Views/Public/FundraiserPage"));
// CDM Round 2 (G-GD) — public giving-day landing page.
const GivingDayPage = React.lazy(() => import("./Views/Public/GivingDayPage"));
// CDM Round 2 (G-TEAM) — public P2P team page.
const TeamPage = React.lazy(() => import("./Views/Public/TeamPage"));
// JOB-3723 (CDM Phase 7) — public event + auction pages.
const EventPage = React.lazy(() => import("./Views/Public/EventPage"));
const AuctionPage = React.lazy(() => import("./Views/Public/AuctionPage"));
const EventCheckoutPage = React.lazy(() => import("./Views/Public/EventCheckoutPage"));
// JOB-5275 — dedicated card payment page for a ticket purchase, so a guest
// buyer pays the ticket total on its own page instead of being handed the
// public donation form (or, before that, a login screen).
const EventTicketPaymentPage = React.lazy(() =>
  import("./Views/Public/EventTicketPaymentPage")
);
// JOB-3725 (CDM Phase 9) — public volunteer shift signup.
const PublicVolunteerSignup = React.lazy(() => import("./Views/Public/VolunteerSignup"));
// LIMO-70 — the embeddable limo booking widget page (framed by widget.js on the
// operator's own website, or opened standalone). Explicit .jsx: see the Vite
// extensionless-import note in the repo memory.
const LimoBookingWidget = React.lazy(() => import("./Views/Limo/Widget/BookingWidget.jsx"));
const PublicBooking = React.lazy(() => import("./Views/Booking/PublicBooking.jsx"));
// Marketplace S1 — the Cloud Pro Connect listing-claim flow (plain axios, own theme).
const ClaimListing = React.lazy(() => import("./Views/ProConnect/ClaimListing.jsx"));
// Marketplace S3 — the rest of Cloud Pro Connect: the pro-facing landing, find
// my listing and create a listing. Same rules as the claim page.
const ProConnectLanding = React.lazy(() => import("./Views/ProConnect/ProConnectLanding.jsx"));
const FindMyListing = React.lazy(() => import("./Views/ProConnect/FindMyListing.jsx"));
const CreateListing = React.lazy(() => import("./Views/ProConnect/CreateListing.jsx"));
// LIMO-107 — the passenger's live tracking page. Explicit .jsx: an
// extensionless lazy import can resolve a same-named .js and come back undefined.
const LimoTrackPage = React.lazy(() => import("./Views/Limo/Track/TrackPage.jsx"));
// LIMO-140 — a partner operator's farm-out offer, from the link in the offer email or text.
const LimoFarmOfferPage = React.lazy(() => import("./Views/Limo/FarmOffer/FarmOfferPage.jsx"));
// B5 (JOB-6873) — the customer's visit tracking page, opened from the "your
// tech is on the way" text. Explicit .jsx for the same reason as the line
// above: an extensionless lazy import can resolve a same-named .js.
const VisitTrackPage = React.lazy(() => import("./Views/Track/VisitTrackPage.jsx"));
// LIMO-67 — where the "add your card" link in a passenger's email lands. NOT
// framable, unlike the booking widget: it collects a payment credential.
const LimoAddCard = React.lazy(() => import("./Views/Limo/AddCard/AddCardPage.jsx"));
// JOB-4954 — clean shareable donate URL: /donate/:CompanyUrl resolves the
// tenant's public-donation token and forwards to the /donation page.
const DonateBySlug = React.lazy(() => import("./Views/Public/DonateBySlug"));
const TermsOfService = React.lazy(() => import("./Views/Public/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./Views/Public/PrivacyPolicy"));
// AIChat is rendered inside MuiTable (the post-login MainNav), so an extra
// top-level mount here is a redundant duplicate that pulls the chat panel
// into the entry chunk. Layouts own the chat now — main.jsx doesn't need it.
import { Toaster } from "react-hot-toast";
// JOB-6228: this file is the HOST of the toast system, not a caller that
// skipped the shared component. It mounts both providers (<Toaster> for
// react-hot-toast, <ToastContainer> for react-toastify) and needs the raw
// `toast` only for toast.dismiss() in ToastRouteCleanup below. Any scan that
// counts `from "react-toastify"` will flag it; there is nothing to convert.
import { toast } from "react-toastify";
import AppToastContainer from "./components/Toast/AppToastContainer";
import "react-toastify/dist/ReactToastify.css";
// leaflet/dist/leaflet.css used to live here; moved into the 3 map files
// that actually use leaflet (WorkerLocationMap, LiveMapPanel, VisitDetailTabs)
// so the login chunk doesn't ship leaflet's stylesheet.
import "./components/Toast/style.css";
import "./components/Toast/Undo.css";
import { wasToastJustShown } from "./components/Toast/Toster.jsx";
import { StaffProvider } from "./components/StaffData/Staffdata";
import { UserTypeProvider } from "./contexts/UserTypeContext";
import { DejavooPaymentProvider } from "./contexts/DejavooPaymentContext";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import RouteProgressBar from "./components/RouteProgressBar/RouteProgressBar";
import { suppressAllErrors } from "./utils/errorSuppression";
import { restoreAuthFromHash } from "./utils/authRestore";
import { reloadOnceForStaleChunk } from "./utils/staleChunkReload";
import { startVersionWatch } from "./utils/appVersion";
// JOB-7106: a company's brand on its public pages (paints only when a page names its company).
import { PublicBrandProvider } from "./contexts/PublicBrandContext";
// Marketplace S3 — a leaf module (no imports), so the "/" switch below costs nothing.
import { isProConnectHost } from "./Views/ProConnect/proConnectHost.js";
import { HideUnlessMarketplace, useMarketplaceProduct } from "./hooks/useMarketplaceProduct.js";
// "Confirm it's you" before sensitive actions (utils/stepUp.js).
import StepUpDialog from "./components/StepUp/StepUpDialog.jsx";

// Marketplace S3 — "/" is the Cloud Pro Connect landing on the cloudproconnect
// host and the sign-in page everywhere else. The landing also waits for the
// product switch (S9): dark until GET /v1/marketplace/product says enabled.
const RootRoute = () => {
  const { enabled, loaded } = useMarketplaceProduct();
  if (!isProConnectHost()) return <Navigate to="/auth/login" replace />;
  if (!loaded) return null;
  if (!enabled) return <Navigate to="/auth/login" replace />;
  return <ProConnectLanding />;
};

// Signup Gateway (signup-gateway PLAN §3 Entry points): old marketing links and
// the gym site's 301 point at /signup, which otherwise falls into the
// /:CompanyUrl/* route and lands on login with the query gone. Send it to
// /auth/signup with the whole query; that page keeps only ref and utm_*.
const SignupPathRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/auth/signup${search || ""}`} replace />;
};

// =========================================================================
// Stale-chunk auto-recovery (see src/utils/staleChunkReload.js for the full
// story). Vite's preload helper wraps every dynamic import() in the build
// and dispatches "vite:preloadError" on window when a chunk or one of its
// css/js deps fails to fetch. Reload once onto the current build — the user
// sees a normal page load instead of an error screen. preventDefault()
// stops the failure from also propagating to React.lazy/ErrorBoundary
// while the reload is underway. If the reload cooldown was already spent
// (reload didn't cure it), the error propagates and ErrorBoundary shows
// its "new version available" screen instead of reload-looping.
// =========================================================================
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnceForStaleChunk()) {
    event.preventDefault();
  }
});

/** Aligns MUI with `index.css` Cloud Job Manager style guide tokens */
const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h3: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
    },
    subtitle2: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
    body2: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
    button: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      textTransform: "none",
    },
    caption: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
    overline: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
  },
  palette: {
    primary: {
      main: "#2563eb", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
      light: "#dbeafe", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
      dark: "#1d4ed8", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
    },
    secondary: {
      main: "#d4943a",
      light: "#fef3e2",
      dark: "#b45309",
    },
    error: {
      main: "#dc2626",
      light: "#fee2e2",
    },
    success: {
      main: "#059669",
      light: "#d1fae5",
    },
    warning: {
      main: "#d97706",
      light: "#fef9c3",
    },
    info: {
      main: "#2563eb", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
      light: "#dbeafe", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
      disabled: "#9ca3af",
    },
    divider: "#e5e7eb",
    background: {
      default: "#f7f8fa",
      paper: "#ffffff",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          fontFamily: '"Inter", "Segoe UI", sans-serif',
          fontWeight: 600,
          fontSize: "12px",
          lineHeight: 1.25,
          height: "auto",
          minHeight: 24,
          padding: "4px 12px",
          borderRadius: "9999px",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// SECURITY HISTORY (2026-04-20 → 2026-04-21): the prototype-freeze
// mitigation for the xlsx prototype-pollution CVE (GHSA-4r6h-8v6p-xvw6)
// was tried and reverted — d3-color (via recharts) writes to
// prototype.constructor at module init, which threw on frozen
// Object.prototype and killed every dashboard with a chart.
//
// The xlsx dependency itself has since been removed. All spreadsheet
// reads/writes now go through src/utils/xlsxHelpers.js, which wraps
// exceljs. Nothing in the tree should be pulling in `xlsx` — if it
// comes back, the CVE is open again.

// Bridges React Router's `navigate` into the axios instance so the 401
// interceptor can do SPA navigation instead of `window.location.href`. Also
// listens for force-logout events any module can dispatch — the goal is no
// hard reloads anywhere; loading skeletons handle the visual transition.
const AxiosNavigatorBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setAxiosNavigator(navigate);
    const handleForceLogout = () => {
      navigate("/auth/login", { replace: true });
    };
    window.addEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
    return () => {
      setAxiosNavigator(null);
      window.removeEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
    };
  }, [navigate]);
  // An expired session returns to the login page without waiting for the
  // user to click something (see startSessionWatch in utils/axiosInstance).
  // Its own effect: navigate's identity changes on route changes, the watcher
  // should start once.
  useEffect(() => startSessionWatch(), []);
  // Reload when a new build is published (src/utils/appVersion.js).
  useEffect(() => startVersionWatch(), []);
  return null;
};

// JOB-3936 — clear any lingering toasts on route change so a stale
// success/error notification from a previous page doesn't carry over
// onto the next one. Runs on every pathname change.
// JOB-4608 — but spare toasts fired within the last second: save flows fire
// their success toast and navigate in the same tick (Add User →
// navigate(-1)), so the blanket dismiss was killing the feedback before it
// was ever visible. A just-fired toast is the new page's feedback, not a
// stale carry-over; it still auto-closes after 2s on its own.
const ToastRouteCleanup = () => {
  const location = useLocation();
  useEffect(() => {
    if (!wasToastJustShown()) toast.dismiss();
  }, [location.pathname]);
  return null;
};

// JOB-5217: reset the root ErrorBoundary on SPA route change. Previously the
// boundary was mounted ABOVE BrowserRouter and had no visibility into
// navigation, so once any page threw during render (e.g. the Send New Email
// page's null read) the "Something went wrong" screen persisted on every
// subsequent route until a hard refresh. Mounting the boundary INSIDE the
// router and feeding it the current pathname as a resetKey clears the caught
// error the instant the user navigates elsewhere. The stale-chunk auto-recovery
// branch is untouched: a failed chunk import triggers a full page reload (not an
// SPA pathname change), and ErrorBoundary additionally refuses to reset a
// stale-chunk state on resetKeys change.
const RouteAwareErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary resetKeys={[location.pathname]}>{children}</ErrorBoundary>
  );
};

// =========================================================================
// Background asset-cache warmer — permanent fix for
// "Failed to fetch dynamically imported module" on long-open tabs.
//
// The build publishes /preload-manifest.json (see scripts/atomic-publish.mjs)
// listing every hashed js/css chunk of THIS build. Shortly after the app
// loads, fetch each one once at low priority: the assets are served with
// Cache-Control: immutable, so they land in the browser's HTTP cache. From
// then on the tab satisfies every lazy route import from its own cache and
// never needs the server for a chunk again — deploys, server-side pruning or
// redeploys can no longer break an already-open tab, with no reload and no
// user-facing prompt.
//
// Guards: production builds only (dev serves no manifest), skipped on
// Data-Saver connections, one warm per build per browser (localStorage key),
// and every failure is swallowed — this must never affect the app itself.
// =========================================================================
function warmAssetCache() {
  if (!import.meta.env.PROD) return;
  try {
    if (navigator.connection && navigator.connection.saveData) return;
  } catch (_) {}
  const run = async () => {
    try {
      const res = await fetch("/preload-manifest.json", { cache: "no-store" });
      if (!res.ok) return;
      const names = await res.json();
      if (!Array.isArray(names) || names.length === 0) return;
      const buildKey = `${names.length}:${names[0]}:${names[names.length - 1]}`;
      if (localStorage.getItem("cs-warmed-build") === buildKey) return;
      let i = 0;
      const worker = async () => {
        while (i < names.length) {
          const name = names[i++];
          try {
            await fetch(`/assets/${encodeURIComponent(name)}`, {
              cache: "force-cache",
              priority: "low",
            });
          } catch (_) {
            // A single failed file is fine — the tab is no worse off than
            // it was without warming.
          }
        }
      };
      await Promise.all(Array.from({ length: 4 }, worker));
      localStorage.setItem("cs-warmed-build", buildKey);
    } catch (_) {
      // Never let cache warming interfere with the app.
    }
  };
  // Wait for the app to be fully loaded + a quiet moment so warming never
  // competes with real page loads for bandwidth.
  const schedule = () => setTimeout(run, 5000);
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
}
warmAssetCache();

// Restore auth data before rendering. Login review item 3: this may redeem a
// support-session hand-off code with the API, so the first render waits for it
// (never long -- one request, and a failure still renders).
const authRestored = restoreAuthFromHash();

// Initialize error suppression - suppresses all errors from screen and console
suppressAllErrors();

const root = ReactDOM.createRoot(document.getElementById("root"));

authRestored.finally(() => root.render(
  <BrowserRouter>
    <RouteAwareErrorBoundary>
      <AuthProvider>
      <UserTypeProvider>
          <DejavooPaymentProvider>
          <StaffProvider>
            <ThemeProvider theme={theme}>
            <AxiosNavigatorBridge />
            <ToastRouteCleanup />
            {/* JOB-4493: thin top progress bar on every route change so
                navigation between heavy pages gives an immediate loading cue
                instead of leaving the previous page's content looking frozen.
                Complements the layout's AdminPageSkeleton (which only covers
                lazy-chunk downloads, not data fetches on cached chunks). */}
            <RouteProgressBar />
            {/* Outer Suspense catches the few standalone lazy routes
                (Plan, PlanPurchase, InvoicePayment, LinkPayment, Public
                pages). Layouts are eager so the app shell renders
                instantly — no double-spinner between boot loader and
                page. Per-route lazy is caught by each layout's own
                inner Suspense (fallback={null}). */}
            <React.Suspense fallback={null}>
            <PublicBrandProvider>
            <Routes>
            <Route path="/auth/*" element={<Auth />} />

            {/* Super admin isolation (docs/SUPERADMIN-ISOLATION.md): the
                super-admin portal is NOT part of this (tenant) app. It is its
                own entry (superadmin.html -> src/superadmin/main.jsx), served by
                the API only at /<SUPER_ADMIN_PATH> on the super-admin host. */}

            {/* Customer Routes - Protected */}
            {/* Customer Routes - Protected */}
            <Route
              path="/:CompanyUrl/customers/*"
              element={
                <ProtectedRoute
                  requiredRole="Customer"
                  tokenKey="customerToken"
                >
                  <Client />
                </ProtectedRoute>
              }
            />

            {/* Donor Routes - Protected */}
            <Route
              path="/:CompanyUrl/donors/*"
              element={
                <ProtectedRoute requiredRole="Customer" tokenKey="donorToken">
                  <Donor />
                </ProtectedRoute>
              }
            />

            {/* Staff Member Routes - Protected */}
            <Route
              path="/:CompanyUrl/staff-member/*"
              element={
                <ProtectedRoute requiredRole="Worker" tokenKey="workerToken">
                  <StaffMember />
                </ProtectedRoute>
              }
            />

            {/* Admin/Company Routes - Protected */}
            <Route
              path="/:CompanyUrl/*"
              element={
                <ProtectedRoute requiredRole="Company" tokenKey="adminToken">
                  <Admin />
                </ProtectedRoute>
              }
            />

            {/* Public Routes */}
            <Route path="/signup" element={<SignupPathRedirect />} />
            <Route path="/donation" element={<PublicDonation />} />
            {/* JOB-4954 — clean slug-based donate link. */}
            <Route path="/donate/:CompanyUrl" element={<DonateBySlug />} />
            <Route path="/unsubscribe" element={<NewsletterUnsubscribe />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/plan-purchase" element={<PlanPurchase />} />
            {/* JOB-3721 (CDM Phase 5) — public P2P fundraiser pages. */}
            <Route path="/fundraiser/:slug" element={<FundraiserPage />} />
            {/* CDM Round 2 (G-GD) — public giving-day landing pages. */}
            <Route path="/giving-day/:slug" element={<GivingDayPage />} />
            {/* CDM Round 2 (G-TEAM) — public P2P team pages. */}
            <Route path="/team/:slug" element={<TeamPage />} />
            {/* JOB-3723 (CDM Phase 7) — public event + auction pages. */}
            <Route path="/event/:slug" element={<EventPage />} />
            <Route
              path="/event/:slug/auction/:AuctionItemId"
              element={<AuctionPage />}
            />
            <Route
              path="/event/:slug/checkout"
              element={<EventCheckoutPage />}
            />
            {/* JOB-5275 — ticket payment. Public: a buyer with no account must
                be able to reach it. */}
            <Route
              path="/event/:slug/pay"
              element={<EventTicketPaymentPage />}
            />
            {/* JOB-3725 (CDM Phase 9) — public volunteer shift signup. */}
            <Route path="/volunteer/:slug" element={<PublicVolunteerSignup />} />
            {/* LIMO-70 — public online booking widget. Public: a passenger with no
                account books here. Embeddable: see index.html / vite.config.js. */}
            <Route path="/limo/book/:alias" element={<LimoBookingWidget />} />
            {/* C3 (JOB-6878) — the job-mode booking page. Public: the person
                filling this in has no account and is not getting one. The page
                uses plain axios, not axiosInstance, so no token is attached and
                no 401 interceptor can bounce a customer to a login screen. */}
            <Route path="/book/:alias" element={<PublicBooking />} />
            {/* Marketplace S1 — a contractor claims their Find Pros Near Me
                listing and gets a free Cloud Job Manager account. Public: the
                person has no account yet. Plain axios, like /book above. */}
            <Route path="/claim/:slug" element={<HideUnlessMarketplace><ClaimListing /></HideUnlessMarketplace>} />
            {/* Marketplace S3 — Cloud Pro Connect for pros: the landing (also
                "/" on the cloudproconnect host), find my listing, create a
                listing. Public, plain axios. Prefixed and hyphenated on purpose:
                a bare word (/find, /join) would equal the CompanyUrl of a
                company with that name and shadow its admin URLs. Only these
                three exact paths are static, so /pro-connect/<anything else>
                still reaches /:CompanyUrl/*. */}
            <Route path="/pro-connect" element={<HideUnlessMarketplace><ProConnectLanding /></HideUnlessMarketplace>} />
            <Route path="/pro-connect/find" element={<HideUnlessMarketplace><FindMyListing /></HideUnlessMarketplace>} />
            <Route path="/pro-connect/join" element={<HideUnlessMarketplace><CreateListing /></HideUnlessMarketplace>} />
            {/* LIMO-67 — the passenger adds their own card from an emailed link,
                so the number is never read down the phone. Deliberately NOT in
                the framing exemptions: this page must not be embeddable. */}
            <Route path="/limo/add-card" element={<LimoAddCard />} />
            {/* LIMO-107 — the passenger's live tracking page, opened from a link
                with a token in it. Public: the passenger has no account. NOT in
                the framing exemptions — a page that shows where a car is has no
                business being embedded in someone else's site. */}
            <Route path="/track/:token" element={<LimoTrackPage />} />
            {/* LIMO-140 — a partner operator's farm-out offer, opened from a link
                with a token in it. Public: the partner has no account here. NOT
                framable: accepting a trip is not something to do inside someone
                else's page. */}
            <Route path="/farm-offer/:token" element={<LimoFarmOfferPage />} />
            {/* B5 (JOB-6873) — the customer's visit tracking page, opened
                from a texted link with a token in it. Public: the customer has
                no account. NOT framable, for the same reason as the line
                above — a page showing where a tech is has no business being
                embedded in someone else's site. */}
            <Route path="/track-visit/:token" element={<VisitTrackPage />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/InvoicePayment" element={<InvoicePayment />} />
            <Route path="/LinkPayment" element={<LinkPayment />} exact />
          </Routes>
            </PublicBrandProvider>
          </React.Suspense>
          <StepUpDialog />
          <Toaster
            position="top-center"
            containerClassName="rht-toaster-root"
            toastOptions={{
              duration: 3000,
              style: {
                background: "var(--toast-dark-bg)",
                color: "var(--text-white-color)",
                maxWidth: "90vw",
                minWidth: "300px",
                fontSize: "14px",
                padding: "16px",
                borderRadius: "8px",
                boxShadow: "var(--shadow-md)",
              },
              success: {
                style: {
                  background: "var(--green)",
                },
              },
              error: {
                style: {
                  background: "var(--red)",
                },
              },
              loading: {
                style: {
                  background: "var(--blue-primary)",
                },
              },
            }}
          />
          <AppToastContainer />
        </ThemeProvider>
      </StaffProvider>
          </DejavooPaymentProvider>
    </UserTypeProvider>
      </AuthProvider>
    </RouteAwareErrorBoundary>
  </BrowserRouter>
));
